import { observer } from 'mobx-react'
import styles from './VideoBox.module.less'
import { useRef, useState, useCallback, useEffect } from 'react'
import classNames from 'classnames'
import { IconImage } from '../Icon'

interface VideoBoxProps {
  src?: string
  poster?: string
  trigger?: 'click' | 'hover'
  style?: React.CSSProperties
  loop?: boolean
}

const VideoBox = observer(
  ({ src, poster, trigger = 'click', style, loop = false }: VideoBoxProps) => {
    // -1: 未播放，0: 暂停，1: 播放
    const [isPlaying, setIsPlaying] = useState(-1)
    const videoRef = useRef<HTMLVideoElement>(null)
    const isPlayRequestedRef = useRef(false)
    const isLoadingRef = useRef(false)
    // 跟踪播放请求以便可以取消
    const playPromiseRef = useRef<Promise<void> | null>(null)
    // 用于跟踪鼠标悬停计时器
    const hoverTimerRef = useRef<NodeJS.Timeout | null>(null)
    // 跟踪是否正在执行停止操作
    const isStoppingRef = useRef(false)

    // 清理函数，取消所有未完成的播放请求
    const cleanupPlayRequests = useCallback(() => {
      isPlayRequestedRef.current = false
      isLoadingRef.current = false
      playPromiseRef.current = null
    }, [])

    // 清理鼠标悬停计时器
    const clearHoverTimer = useCallback(() => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current)
        hoverTimerRef.current = null
      }
    }, [])

    // 当src改变时重置状态
    useEffect(() => {
      setIsPlaying(-1)
      cleanupPlayRequests()
      clearHoverTimer() // 确保清除计时器
      isStoppingRef.current = false // 重置停止状态
    }, [src, cleanupPlayRequests, clearHoverTimer])

    // 组件卸载时清理
    useEffect(() => {
      const video = videoRef.current
      return () => {
        if (video && !video.paused) {
          try {
            video.pause()
          } catch (e) {
            console.error('停止视频失败:', e)
          }
        }
        cleanupPlayRequests()
        clearHoverTimer() // 确保清除计时器
        isStoppingRef.current = false
      }
    }, [cleanupPlayRequests, clearHoverTimer])

    // 统一处理播放逻辑
    const playVideo = useCallback(() => {
      // 如果正在执行停止操作或没有视频，不执行播放
      if (!src || !videoRef.current || isStoppingRef.current) return

      // 如果已经有播放请求或正在加载，避免重复请求
      if (isPlayRequestedRef.current || isLoadingRef.current) return

      isPlayRequestedRef.current = true
      isLoadingRef.current = true

      setIsPlaying(1)

      try {
        // 存储播放请求以便可以跟踪其状态
        const playPromise = videoRef.current.play()
        playPromiseRef.current = playPromise

        playPromise
          .then(() => {
            // 在播放成功后再次检查是否应该停止，防止鼠标已经离开但播放才完成的情况
            if (playPromiseRef.current === playPromise) {
              if (isStoppingRef.current) {
                // 如果这时应该停止，立即停止播放
                videoRef.current?.pause()
                if (videoRef.current) videoRef.current.currentTime = 0
                setIsPlaying(-1)
                console.log('播放完成后立即停止，因为已触发停止操作')
              } else {
                isLoadingRef.current = false
              }
            }
          })
          .catch((error) => {
            // 只有当这是最新的播放请求时才处理错误
            if (playPromiseRef.current === playPromise) {
              // 判断是否是AbortError
              if (error.name === 'AbortError') {
                console.warn('视频播放被中断，可能是组件已卸载或src已更改:', error)
              } else {
                console.error('播放失败:', error)
              }

              setIsPlaying(0)
              isLoadingRef.current = false
            }
          })
          .finally(() => {
            // 只有当这是最新的播放请求时才清理
            if (playPromiseRef.current === playPromise) {
              isPlayRequestedRef.current = false
            }
          })
      } catch (error) {
        console.error('调用play()方法失败:', error)
        setIsPlaying(0)
        isLoadingRef.current = false
        isPlayRequestedRef.current = false
      }
    }, [src])

    // 统一处理暂停逻辑
    const pauseVideo = useCallback(() => {
      if (!src || !videoRef.current || isPlayRequestedRef.current) return

      if (!videoRef.current.paused) {
        videoRef.current.pause()
        setIsPlaying(0)
      }
    }, [src])

    // 统一处理停止逻辑（回到初始状态）
    const stopVideo = useCallback(() => {
      // 移除阻止stopVideo执行的isPlayRequestedRef检查，确保无论如何都能停止视频
      if (!src || !videoRef.current) return

      // 标记当前要执行停止操作
      const wasRequested = isPlayRequestedRef.current

      // 标记正在执行停止操作，防止新的播放请求
      isStoppingRef.current = true

      // 立即清理播放请求状态，以防止冲突
      cleanupPlayRequests()

      // 如果视频在播放，暂停它
      if (!videoRef.current.paused) {
        try {
          videoRef.current.pause()
        } catch (error) {
          console.error('停止视频失败:', error)
        }
      }

      // 重置视频到初始状态
      if (videoRef.current.currentTime > 0) {
        try {
          videoRef.current.currentTime = 0
        } catch (error) {
          console.error('重置视频时间失败:', error)
        }
      }

      // 更新UI状态
      setIsPlaying(-1)

      // 如果有必要，记录调试信息
      if (wasRequested) {
        console.log('取消了正在进行的播放请求')
      }

      // 延迟重置停止状态，确保不会立即触发新的播放
      setTimeout(() => {
        isStoppingRef.current = false
      }, 50)
    }, [src, cleanupPlayRequests])

    // 切换播放/暂停状态
    const togglePlay = useCallback(() => {
      if (!videoRef.current) return

      if (videoRef.current.paused) {
        playVideo()
      } else {
        pauseVideo()
      }
    }, [playVideo, pauseVideo])

    const onMouseEnter = useCallback(() => {
      if (trigger === 'hover' && src) {
        // 清除可能存在的计时器
        clearHoverTimer()

        // 设置新的计时器，延迟300ms执行播放
        hoverTimerRef.current = setTimeout(() => {
          playVideo()
          hoverTimerRef.current = null
        }, 80)
      }
    }, [trigger, src, playVideo, clearHoverTimer])

    const onMouseLeave = useCallback(() => {
      if (trigger === 'hover' && src) {
        // 清除计时器，取消尚未开始的播放
        clearHoverTimer()

        // 无条件停止视频，不再检查isPlaying状态
        stopVideo()
      }
    }, [trigger, src, stopVideo, clearHoverTimer])

    const onMouseClick = useCallback(() => {
      if (trigger === 'click' && src) {
        togglePlay()
      }
    }, [trigger, src, togglePlay])

    const handleVideoEnded = useCallback(() => {
      if (!loop) {
        setIsPlaying(0)
      }
    }, [loop])

    // 优化错误处理
    const handleError = useCallback(() => {
      console.log('视频加载错误')
      setIsPlaying(-1)
      cleanupPlayRequests()
    }, [cleanupPlayRequests])

    return (
      <div
        className={styles.videoBox}
        style={style}
        onContextMenu={(e) => e.preventDefault()}
        key={src}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onMouseClick}
      >
        {src && (
          <video
            ref={videoRef}
            className={classNames(styles.video, { [styles.hide]: isPlaying === -1 })}
            poster={poster}
            controls={false}
            controlsList='nodownload disablePictureInPicture noremoteplayback nofullscreen noplaybackrate'
            muted={true}
            x5-video-player-type='h5'
            x5-playsinline='true'
            webkit-playsinline='true'
            playsInline={true}
            disablePictureInPicture={true}
            preload='none'
            loop={loop}
            src={src}
            onCanPlay={() => console.log('can play')}
            onError={handleError}
            onEnded={handleVideoEnded}
            onContextMenu={(e) => e.preventDefault()}
          >
            <source src={src} type='video/mp4' />
          </video>
        )}

        <div className={classNames(styles.videoBoxMask, { [styles.hide]: isPlaying === 1 })}>
          <img
            className={classNames(styles.videoBoxMaskImg)}
            src={poster}
            alt='播放封面'
            draggable={false}
            loading='lazy'
            style={{
              display: isPlaying === -1 ? 'block' : 'none',
            }}
          />

          {trigger === 'click' && src && (
            <IconImage className={styles.videoBoxMaskPlay} src='Detail_IconPlayVideo' />
          )}
        </div>
      </div>
    )
  }
)

export default VideoBox
